
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';
import { Search, Plus, Minus, Settings } from 'lucide-react';
import PlayerRegistrationModal from './PlayerRegistrationModal';

interface Player {
  user_id: string;
  position?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  user_type?: string;
  user_profile?: {
    id: string;
    username?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
  } | null;
}

interface PlayerManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  teamSport?: string;
  currentPlayers: Player[];
  onPlayersUpdate: () => void;
}

const SPORT_POSITIONS = {
  'Soccer': ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'],
  'Football': ['Quarterback', 'Running Back', 'Wide Receiver', 'Tight End', 'Offensive Line', 'Defensive Line', 'Linebacker', 'Cornerback', 'Safety', 'Kicker', 'Punter'],
  'Basketball': ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  'Baseball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field'],
  'Softball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field'],
  'Hockey': ['Center', 'Left Wing', 'Right Wing', 'Left Defense', 'Right Defense', 'Goalie'],
  'Volleyball': ['Setter', 'Outside Hitter', 'Middle Blocker', 'Opposite Hitter', 'Libero', 'Defensive Specialist'],
  'Tennis': ['Singles Player', 'Doubles Player'],
  'Track & Field': ['Sprinter', 'Distance Runner', 'Hurdler', 'Jumper', 'Thrower', 'Multi-Event'],
  'Swimming': ['Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley', 'Distance'],
  'Lacrosse': ['Attack', 'Midfield', 'Defense', 'Goalie'],
  'Rugby': ['Forward', 'Back', 'Scrum Half', 'Fly Half', 'Full Back'],
  'Cricket': ['Batsman', 'Bowler', 'Wicket Keeper', 'All Rounder'],
  'Other': ['Player', 'Starter', 'Bench', 'Captain', 'Co-Captain']
};

const getPositionsForSport = (sport?: string): string[] => {
  if (!sport) return ['Player', 'Starter', 'Bench', 'Captain', 'Co-Captain', 'Other'];
  return SPORT_POSITIONS[sport as keyof typeof SPORT_POSITIONS] || ['Player', 'Starter', 'Bench', 'Captain', 'Co-Captain', 'Other'];
};

const PlayerManagementModal = ({ 
  isOpen, 
  onClose, 
  teamId, 
  teamName,
  teamSport,
  currentPlayers, 
  onPlayersUpdate 
}: PlayerManagementModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  
  const availablePositions = getPositionsForSport(teamSport);

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .in('role', ['player', 'parent'])
        .limit(10);

      if (supabaseError) {
        console.error('Error searching Supabase users:', supabaseError);
      }

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_users')
        .select('id, username, role, age_group')
        .or(`username.ilike.%${searchTerm}%`)
        .eq('role', 'player')
        .limit(10);

      if (internalError) {
        console.error('Error searching internal users:', internalError);
      }

      // Combine and format results
      const combined = [
        ...(supabaseUsers || []).map(user => ({
          ...user,
          user_id: user.id,
          user_type: 'supabase'
        })),
        ...(internalUsers || []).map(user => ({
          ...user,
          user_id: user.id,
          user_type: 'internal'
        }))
      ];

      // Filter out users who are already players
      const currentPlayerIds = currentPlayers.map(p => p.user_id);
      const filtered = combined.filter(user => !currentPlayerIds.includes(user.user_id));

      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error("Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const addPlayer = async (userId: string) => {
    if (!selectedPosition) {
      toast.error("Please select a position");
      return;
    }

    console.log('Adding player:', { userId, teamId, selectedPosition });
    console.log('Starting addPlayer function');

    try {
      await teamService.addPlayerToTeam(teamId, userId, selectedPosition);
      toast.success("Player added successfully");
      onPlayersUpdate();
      setSearchTerm('');
      setSelectedPosition('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error("Failed to add player");
    }
  };

  const removePlayer = async (userId: string) => {
    try {
      await teamService.removePlayerFromTeam(teamId, userId);
      toast.success("Player removed successfully");
      onPlayersUpdate();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error("Failed to remove player");
    }
  };

  // Helper function to get display name for players
  const getPlayerDisplayName = (player: Player): string => {
    if (player.user_profile) {
      if (player.user_type === 'internal') {
        return player.user_profile.username || 'Internal User';
      } else {
        return player.user_profile.username || 
               `${player.user_profile.first_name || ''} ${player.user_profile.last_name || ''}`.trim() || 
               'Unknown User';
      }
    }
    
    // Fallback to existing data structure
    return player.username || 
           `${player.first_name || ''} ${player.last_name || ''}`.trim() || 
           `Player ${player.user_id.substring(0, 8)}`;
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <div className="flex flex-col items-start">
                <span>Manage Team Players</span>
                <span className="text-sm text-gray-400 font-normal">{teamName}</span>
              </div>
              <Button
                onClick={() => setShowRegistrationModal(true)}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Settings className="h-4 w-4 mr-1" />
                Registration Status
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add Players Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-sports-green">Add Players Directly</h3>
              <p className="text-sm text-gray-400">Search and add players directly to your team roster. Players will be added immediately without requiring approval.</p>
              <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-3 mt-2">
                <p className="text-blue-200 text-sm">
                  <strong>Note:</strong> For player requests that need approval, players must use the "Request to Join Team" feature from the team's public page. Those requests will appear in the "Pending Requests" section.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="search" className="text-white">Search Users</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by username or name..."
                      className="pl-10 bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="position" className="text-white">Position</Label>
                  <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {availablePositions.map((position) => (
                        <SelectItem key={position} value={position} className="text-white">
                          {position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Search Results */}
              {availableUsers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white">Search Results</Label>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {availableUsers.map((user) => (
                      <div
                        key={user.user_id}
                        className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-medium text-white">
                              {user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}
                            </p>
                            <p className="text-sm text-gray-400">
                              {user.role} • {user.user_type === 'internal' ? 'new school' : 'old school'}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('=== ADD BUTTON CLICKED ===');
                            console.log('Button clicked!', { 
                              userId: user.user_id, 
                              selectedPosition, 
                              isDisabled: !selectedPosition,
                              teamId,
                              user 
                            });
                            if (selectedPosition) {
                              addPlayer(user.user_id);
                            } else {
                              console.log('Position not selected, button should be disabled');
                            }
                          }}
                          disabled={!selectedPosition}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed relative z-10"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Current Players Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-sports-green">Current Players</h3>
              <p className="text-sm text-gray-400">Players currently on the team roster.</p>
              
              {currentPlayers.length > 0 ? (
                <div className="space-y-2">
                  {currentPlayers.map((player) => (
                    <div
                      key={player.user_id}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium text-white">
                            {getPlayerDisplayName(player)}
                          </p>
                          <div className="flex items-center gap-2">
                            {player.position && (
                              <Badge variant="outline" className="text-sports-green border-sports-green">
                                {player.position}
                              </Badge>
                            )}
                            <span className="text-xs text-gray-400">
                              {player.user_type === 'internal' ? 'Internal User' : 'Supabase User'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => removePlayer(player.user_id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Minus className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No players added yet.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PlayerRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        teamId={teamId}
        teamName={teamName}
      />
    </>
  );
};

export default PlayerManagementModal;
